// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import PersonAddLine from "@store/Library/Components/Toolkit/Icons/PersonAdd/PersonAddLine";
import TextSeparator, { TextSeparatorPresentationProps } from "@store/Library/Components/Toolkit/TextSeparator";
import { signInProps } from "@store/Themes/common/props/signIn";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { useIsNamedContext } from "@toolkit/Framework/Components/NamedContext";
import parse from "html-react-parser";
/* Starter Custom Code - End */

const enum fields {
    text = "text",
    fullWidthButton = "fullWidthButton",
    // Starter Custom Code
    title = "title",
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.fullWidthButton]: boolean;
        // Starter Custom Code
        [fields.showIcon]: boolean;
        [fields.title]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    // Starter Custom Code
    signInCreateNewAccountIcon?: IconPresentationProps;
    signInCreateNewAccountWrapper?: InjectableCss;
    separator?: TextSeparatorPresentationProps;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    signInCreateNewAccountWrapper: signInProps.wrapper,
    signInCreateNewAccountIcon: {
        ...signInProps.icon,
        src: PersonAddLine,
    },
    signInCreateNewAccountTitle: signInProps.title,
    createNewAccountButton: { variant: "secondary" },
    separator: signInProps.separator,
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields: { fullWidthButton, showIcon, title, text }, // Starter Custom Code
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    /* Starter Custom Code - Start */
    const isSignInOverlay = useIsNamedContext(constants.pageType.signInOverlay);
    const styles = React.useMemo(() => {
        if (!isSignInOverlay) {
            return signInCreateNewAccountStyles;
        }

        return mergeProps<SignInCreateNewAccountStyles>(signInCreateNewAccountStyles, {
            signInCreateNewAccountWrapper: signInProps.overlayOverrides?.wrapper,
            signInCreateNewAccountIcon: signInProps?.overlayOverrides?.icon,
            signInCreateNewAccountTitle: signInProps?.overlayOverrides?.title,
        });
    }, [isSignInOverlay]);
    /* Starter Custom Code - End */

    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    // Starter Removed - createButtonCss

    return (
        <>
            {/* Starter Custom Code - Start */}
            {isSignInOverlay && <TextSeparator {...styles.separator}>{translate("OR")}</TextSeparator>}
            {/* Starter Custom Code - End */}
            <StyledWrapper {...styles.signInCreateNewAccountWrapper} /* Starter Custom Code */>
                {/* Starter Custom Code - Start */}
                {showIcon && <Icon {...styles.signInCreateNewAccountIcon} />}
                {title && <Typography {...styles.signInCreateNewAccountTitle}>{title}</Typography>}
                {text && <Typography {...styles.signInCreateNewAccountText}>{parse(text, parserOptions)}</Typography>}
                {/* Starter Custom Code - End */}
                {/* Starter Removed Code - AllowGuestCheckout Button */}
                {createAccountUrl && (
                    <FormActions fullWidth={fullWidthButton} /* Starter Custom Code */>
                        <Button
                            {...styles.createNewAccountButton}
                            // Starter Removed - createButtonCss
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Create Account")}
                        </Button>
                    </FormActions>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            /* Starter Custom Code - Start */
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Create an Account", // Starter Custom Code
                fieldType: "Translatable",
            },
            /* Starter Custom Code - End */
            {
                name: fields.text,
                displayName: "Content", // Starter Custom Code
                editorTemplate: "RichTextField", // Starter Custom Code
                /* Starter Custom Code - Start */
                defaultValue: `<p>Create an account and discover these benefits</p>\n<ul>\n<li>Fast Checkout</li>\n<li>Create and add product to Wishlists</li>\n<li>Edit and View your account information</li>\n<li>Track and Review your Orders</li>\n</ul>`,
                /* Starter Custom Code - End */
                fieldType: "Translatable",
                // Starter Removed - sortOrder
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                // Starter Removed - sortOrder
            },
        ],
    },
};

export default widgetModule;
